import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Filter, X, ChevronUp, ChevronDown, Calendar, MapPin, Building2, Users, RotateCcw, DollarSign, Check } from 'lucide-react';
import { Button } from './ui/button';
import { useFilters } from '../context/FilterContext';
import { AppContext } from '../App';
import { parseDateString } from '../utils/dateUtils';

// Pay Type options for filter dropdown — built dynamically from AppPayType
// table so any pay type marked as Premium in Settings is available here.
const formatMultiplier = (m) => {
  const n = Number(m ?? 1);
  return Number.isInteger(n) ? `${n}` : `${n}`;
};
const buildPayTypeOptions = (premiumPayTypes) =>
  (premiumPayTypes || []).map(pt => ({
    value: pt.pay_type,
    label: `${pt.pay_type} (${formatMultiplier(pt.multiplier)}x)`
  }));

// Multi-select dropdown component
const MultiSelectDropdown = ({ options, selected, onChange, placeholder, testId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const removeTag = (e, value) => {
    e.stopPropagation();
    onChange(selected.filter(v => v !== value));
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C8A]/20 focus:border-[#2E5C8A] bg-white cursor-pointer min-h-[38px] flex items-center flex-wrap gap-1"
        data-testid={testId}
      >
        {selected.length === 0 ? (
          <span className="text-slate-500">{placeholder}</span>
        ) : (
          selected.map(value => {
            const option = options.find(o => o.value === value);
            return (
              <span
                key={value}
                className="inline-flex items-center gap-1 bg-[#2E5C8A]/10 text-[#1e3a5f] px-2 py-0.5 rounded-full text-xs font-medium"
              >
                {option?.label || value}
                <button
                  onClick={(e) => removeTag(e, value)}
                  className="hover:bg-[#2E5C8A]/20 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })
        )}
        <ChevronDown className={`h-4 w-4 ml-auto text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute z-50 w-full bottom-full mb-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto"
          >
            {options.map(option => (
              <div
                key={option.value}
                onClick={() => toggleOption(option.value)}
                className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 transition-colors ${
                  selected.includes(option.value) ? 'bg-[#2E5C8A]/5' : ''
                }`}
                data-testid={`pay-type-option-${option.value}`}
              >
                <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                  selected.includes(option.value) 
                    ? 'bg-[#2E5C8A] border-[#2E5C8A]' 
                    : 'border-slate-300'
                }`}>
                  {selected.includes(option.value) && (
                    <Check className="h-3 w-3 text-white" />
                  )}
                </div>
                <span className="text-sm">{option.label}</span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FloatingFilterPanel = () => {
  const { dimensions, premiumPayTypes } = React.useContext(AppContext);
  const { filters, setFilters, resetFilters, hasActiveFilters, activeFilterCount } = useFilters();
  const [isExpanded, setIsExpanded] = useState(false);

  const payTypeOptions = React.useMemo(
    () => buildPayTypeOptions(premiumPayTypes),
    [premiumPayTypes]
  );

  const formatDateRange = () => {
    if (filters.start_date && filters.end_date) {
      const start = parseDateString(filters.start_date);
      const end = parseDateString(filters.end_date);
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return 'All dates';
  };

  const getActiveFilterTags = () => {
    const tags = [];
    if (filters.location) tags.push({ type: 'location', value: filters.location, icon: MapPin });
    if (filters.department) tags.push({ type: 'department', value: filters.department, icon: Building2 });
    if (filters.manager) tags.push({ type: 'manager', value: filters.manager, icon: Users });
    // Handle multi-select pay_types
    if (filters.pay_types && filters.pay_types.length > 0) {
      const payTypesLabel = filters.pay_types.length === 1 
        ? filters.pay_types[0] 
        : `${filters.pay_types.length} Pay Types`;
      tags.push({ type: 'pay_types', value: payTypesLabel, icon: DollarSign });
    }
    return tags;
  };

  const activeTags = getActiveFilterTags();

  return (
    <div className="fixed bottom-6 right-24 z-50" data-testid="floating-filter-panel">
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="mb-3 bg-white rounded-xl shadow-2xl border border-slate-200 w-[380px] overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2E5C8A] px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <Filter className="h-4 w-4" />
                <span className="font-semibold text-sm">Global Filters</span>
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Applied to all views</span>
              </div>
              <button
                onClick={() => setIsExpanded(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Filter Content */}
            <div className="p-4 space-y-4">
              {/* Date Range */}
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1 mb-2">
                  <Calendar className="h-3 w-3" />
                  Date Range
                </label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={filters.start_date || ''}
                    onChange={(e) => setFilters({ start_date: e.target.value })}
                    className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C8A]/20 focus:border-[#2E5C8A]"
                    data-testid="global-filter-start-date"
                  />
                  <span className="text-slate-400 self-center">to</span>
                  <input
                    type="date"
                    value={filters.end_date || ''}
                    onChange={(e) => setFilters({ end_date: e.target.value })}
                    className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C8A]/20 focus:border-[#2E5C8A]"
                    data-testid="global-filter-end-date"
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1 mb-2">
                  <MapPin className="h-3 w-3" />
                  Location
                </label>
                <select
                  value={filters.location || ''}
                  onChange={(e) => setFilters({ location: e.target.value || null })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C8A]/20 focus:border-[#2E5C8A] bg-white"
                  data-testid="global-filter-location"
                >
                  <option value="">All Locations</option>
                  {dimensions.locations?.map((loc) => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>

              {/* Department */}
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1 mb-2">
                  <Building2 className="h-3 w-3" />
                  Department
                </label>
                <select
                  value={filters.department || ''}
                  onChange={(e) => setFilters({ department: e.target.value || null })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C8A]/20 focus:border-[#2E5C8A] bg-white"
                  data-testid="global-filter-department"
                >
                  <option value="">All Departments</option>
                  {dimensions.departments?.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>

              {/* Manager */}
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1 mb-2">
                  <Users className="h-3 w-3" />
                  Manager
                </label>
                <select
                  value={filters.manager || ''}
                  onChange={(e) => setFilters({ manager: e.target.value || null })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C8A]/20 focus:border-[#2E5C8A] bg-white"
                  data-testid="global-filter-manager"
                >
                  <option value="">All Managers</option>
                  {dimensions.managers?.map((mgr) => (
                    <option key={mgr} value={mgr}>{mgr}</option>
                  ))}
                </select>
              </div>

              {/* Pay Types - Multi-Select */}
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1 mb-2">
                  <DollarSign className="h-3 w-3" />
                  Pay Types
                </label>
                <MultiSelectDropdown
                  options={payTypeOptions}
                  selected={filters.pay_types || []}
                  onChange={(values) => setFilters({ pay_types: values })}
                  placeholder="All Premium Pay Types"
                  testId="global-filter-pay-types"
                />
              </div>

              {/* Reset Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={resetFilters}
                className="w-full mt-2 text-slate-600 hover:text-[#1e3a5f] hover:border-[#2E5C8A]"
                data-testid="global-filter-reset"
              >
                <RotateCcw className="h-3 w-3 mr-2" />
                Reset to Default (12-month lookback)
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button / Collapsed State */}
      <motion.button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`
          flex items-center gap-2 px-4 py-3 rounded-full shadow-lg
          transition-all duration-200 hover:shadow-xl
          ${hasActiveFilters 
            ? 'bg-gradient-to-r from-[#FF8C42] to-[#FF6B35] text-white' 
            : 'bg-gradient-to-r from-[#1e3a5f] to-[#2E5C8A] text-white'
          }
        `}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        data-testid="floating-filter-toggle"
      >
        <Filter className="h-4 w-4" />
        
        {!isExpanded && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{formatDateRange()}</span>
            
            {activeTags.length > 0 && (
              <>
                <div className="w-px h-4 bg-white/30" />
                <div className="flex items-center gap-1">
                  {activeTags.slice(0, 2).map((tag) => (
                    <span 
                      key={`${tag.type}-${tag.value}`} 
                      className="text-xs bg-white/20 px-2 py-0.5 rounded-full truncate max-w-[80px]"
                      title={tag.value}
                    >
                      {tag.value}
                    </span>
                  ))}
                  {activeTags.length > 2 && (
                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                      +{activeTags.length - 2}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        
        {isExpanded ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronUp className="h-4 w-4" />
        )}
        
        {activeFilterCount > 0 && !isExpanded && (
          <span className="absolute -top-1 -right-1 bg-white text-[#FF8C42] text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center shadow">
            {activeFilterCount}
          </span>
        )}
      </motion.button>
    </div>
  );
};

export default FloatingFilterPanel;
